import React, { useState, useRef, useCallback } from 'react';
import { stroot, auth, organisation as organisationAPI } from '@birdi/js-sdk';
import type { MemberWorkspaceSummary } from '@birdi/js-sdk/src/public/organisation';
import { css } from '@emotion/react';
import { BirdiTheme } from '@birdi/theme';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { blocks } from '@birdi/theme';
import { useMetaSelector } from '../meta/reducer';
import { AddWorkspaceModal } from './add-workspace-modal';
import WorkspacesSVG from '@birdi/icons/menu-workspaces.svg';
import { workspaceIconStyle } from '../common/side-menu/SideNavigationDashboard';

interface UserWorkspacesProps {
  trigger: React.ReactNode;
}

export const UserWorkspaces: React.FC<UserWorkspacesProps> = ({ trigger }) => {
  const meta = useMetaSelector();
  const [workspaces, setWorkspaces] = useState<MemberWorkspaceSummary[]>([]);
  const [scopesLoading, setScopesLoading] = useState(false);
  const scopesRequestRef = useRef<'idle' | 'loading' | 'done'>('idle');
  const [showAddWorkspaceModal, setShowAddWorkspaceModal] = useState(false);
  const isPrimaryOrg = !meta.workspace;

  const canCreateWorkspace = meta.permissions.includes(
    'organisation:workspace:create',
  );

  const createWorkspaceEnable =
    meta.features.includes('add-workspaces') &&
    isPrimaryOrg &&
    canCreateWorkspace;

  const onMenuOpenChange = useCallback((open: boolean) => {
    if (!open || scopesRequestRef.current !== 'idle') return;
    scopesRequestRef.current = 'loading';
    setScopesLoading(true);
    organisationAPI
      .getMyWorkspaces(stroot('thuw8a'), { includeMenuIcon: true })
      .then((res) => {
        scopesRequestRef.current = 'done';
        setWorkspaces(res.body?.items ?? []);
      })
      .catch(() => {
        scopesRequestRef.current = 'idle';
      })
      .finally(() => setScopesLoading(false));
  }, []);

  const addNewWorkspace = () => {
    setShowAddWorkspaceModal(true);
  };

  return (
    <>
      <AddWorkspaceModal
        open={showAddWorkspaceModal}
        setOpen={setShowAddWorkspaceModal}
      />
      <DropdownMenu.Root modal={false} onOpenChange={onMenuOpenChange}>
        <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="start"
            alignOffset={-16}
            css={(theme) => [
              blocks.radixDropdownAnimation,
              css`
                display: flex;
                flex-direction: column;
                width: 320px;
                color: ${theme.midnight};
                background: ${theme.mono0};
                box-shadow: 0 4px 8px ${theme.mono40};
                border-radius: 10px;
                overflow: hidden;
                max-height: calc(100vh - 10em);
                overflow-y: auto;
                z-index: 1000;
                hr {
                  width: 100%;
                }
              `,
            ]}
          >
            <div
              css={css`
                padding: 0 1em;
              `}
            >
              <h3
                id="my-workspaces"
                css={css`
                  margin: 0.5em;
                  padding: 0;
                `}
              >
                My workspaces
              </h3>
              <hr
                css={css`
                  margin: 0.5em 0;
                `}
              />
              {scopesLoading && (
                <p
                  css={css`
                    margin: 0.5em;
                    color: inherit;
                    opacity: 0.7;
                  `}
                >
                  Loading workspaces…
                </p>
              )}
              {workspaces.map((ws) => {
                const active = ws.active;
                return (
                  <DropdownMenu.Item key={ws.scopeId} asChild>
                    <button
                      type="button"
                      className="item"
                      css={(theme) => css`
                        width: 100%;
                        margin-bottom: 0.5em;
                        padding: 0.5em;
                        border-radius: 0.25em;
                        white-space: nowrap;
                        text-align: left;
                        background: ${active ? theme.lightOcean : theme.mono0};
                        &:hover {
                          background: ${active
                            ? theme.lightOcean
                            : theme.mono20};
                        }
                        &:focus {
                          outline: none;
                        }
                      `}
                      onClick={() => {
                        auth
                          .switchContext(stroot('aigee1'), {
                            scopeId: ws.scopeId,
                          })
                          .then(() => {
                            window.location.href = '/';
                          });
                      }}
                    >
                      <div
                        css={css`
                          display: flex;
                          justify-content: flex-start;
                          align-items: center;
                        `}
                      >
                        {ws.menuIcon ? (
                          <img
                            src={ws.menuIcon}
                            alt=""
                            css={css`
                              width: 1.5em;
                              height: 1.5em;
                              margin-right: 0.5em;
                              border-radius: 5px;
                              object-fit: cover;
                            `}
                          />
                        ) : (
                          <span
                            css={(theme: BirdiTheme) => workspaceIconStyle(theme)}
                          >
                            <WorkspacesSVG />
                          </span>
                        )}
                        <span
                          css={css`
                            font-weight: bold;
                            margin-left: 0.75em;
                            max-width: 12em;
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            display: inline-block;
                          `}
                        >
                          {ws.name}
                        </span>
                      </div>
                    </button>
                  </DropdownMenu.Item>
                );
              })}
            </div>
            {createWorkspaceEnable && (
              <button
                type="button"
                css={(theme) => css`
                  background-color: ${theme.mono20};
                  width: 100%;
                  padding: 1em 1.5em;
                  text-align: left;
                `}
                onClick={addNewWorkspace}
              >
                + Workspace
              </button>
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </>
  );
};
